import { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Link,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import {
  BoxStyle,
  CheckBoxStyle,
  DataTextField,
  FaCheckCircleIcon,
  LeftGrid,
  OuterBodyGrid,
  rightGridStyle,
} from "../styles";

import {
  providerPortalUrl,
  providerRelationEmail,
} from "../../../constants/Config";
import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import { displayFlex } from "../../../styles/mui/styles/display";
import { formInitialState } from "../../../constants/ReferralRequestor";
import { FormInputText } from "../../../components/formComponents/FormInputText";
import { rules } from "../../../utils/validations/Validations";
import PrivacySecurityConsentModal from "./SecurityModal";
import { CHECK_ELIGIBILITY_WORKFLOW } from "../../../constants/ReferralPaths";
import { CheckboxStyle } from "../styles/SecurityModal";
import { ReferralRequestor } from "../../../models/ReferralRequestor";

export interface PropsFromDispatch {
  saveReferralRequestor: (sessionData: ReferralRequestor) => void;
}
type AllProps = PropsFromDispatch;

const RequestorInformation: React.FC<AllProps> = ({
  saveReferralRequestor,
}: AllProps) => {
  const navigate = useNavigate();
  const [isChecked, setIsChecked] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [clickable, setClickable] = useState<boolean>(false);
  const [isDisagree, setIsDisagree] = useState<boolean>(false);
  const methods = useForm<any>({
    defaultValues: formInitialState,
    mode: "all",
  });
  const {
    control,
    watch,
    formState: { errors },
  } = methods;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
  };

  useEffect(() => {
    sessionStorage.clear();
  }, []);

  useEffect(() => {
    if (isDisagree) {
      setIsChecked(false);
    }
  }, [isDisagree]);

  useEffect(() => {
    if (isChecked === true) {
      setOpenModal(true);
    }
  }, [isChecked]);

  const formValues = watch();

  useEffect(() => {
    const { firstName, lastName, email, isPrivacyCheck } = formValues;
    const validForm =
      firstName &&
      lastName &&
      email &&
      isPrivacyCheck &&
      Object.keys(errors).length === 0;
    setClickable(validForm);
  }, [formValues, errors]);

  const handleClick = () => {
    const requestorInformation = {
      lastName: formValues.lastName,
      firstName: formValues.firstName,
      emailAddress: formValues.email,
      isPrivacyCheck: formValues.isPrivacyCheck
    }
    saveReferralRequestor(requestorInformation);
    setTimeout(() => {
      navigate(CHECK_ELIGIBILITY_WORKFLOW);
    }, 250);
  };

  return (
    <>
      <Grid container sx={OuterBodyGrid}>
        <Grid size={5} sx={LeftGrid}>
          <Grid container direction="column">
            <Grid size={12} sx={BoxStyle}>
              <Typography
                variant="h5"
                color={colors.fonts[20]}
                fontWeight={fontWeight.Weight[6]}
              >
                {`Request a referral via tango's secure Referral Request form.`}
              </Typography>
            </Grid>
            <Grid size={12} sx={BoxStyle}>
              <Box mr={0.9}>
                <FaCheckCircleIcon />
              </Box>
              <Typography variant="subtitle2" fontWeight={fontWeight.Weight[2]}>
                Check eligibility for Home Health services.
              </Typography>
            </Grid>
            <Grid size={12} sx={BoxStyle}>
              <Box mr={0.9}>
                <FaCheckCircleIcon />
              </Box>
              <Typography variant="subtitle2" fontWeight={fontWeight.Weight[2]}>
                Complete the necessary information to request the referral
                electronically.
              </Typography>
            </Grid>
            <Grid size={12} sx={BoxStyle}>
              <Typography variant="subtitle2" fontWeight={fontWeight.Weight[2]}>
                Once submitted, you will receive a confirmation number that you
                can use to check referral status.
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={7} sx={rightGridStyle}>
          <Typography
            variant="h2"
            color={colors.fonts[20]}
            fontWeight={fontWeight.Weight[5]}
          >
            {`WELCOME TO TANGO'S REFERRAL REQUEST SITE`}
          </Typography>
          <Box mt="1.5rem">
            <Typography
              variant="subtitle1"
              color={colors.fonts[20]}
              fontWeight={fontWeight.Weight[5]}
            >
              Name of requester initiating the request
            </Typography>
          </Box>
          <Grid container mt={"2rem"}>
            <Grid size={4} mr={"2rem"}>
              <FormInputText
                name="lastName"
                label="Last Name:"
                control={control}
                helper={rules.lastName}
                InputStyle={DataTextField}
              />
            </Grid>
            <Grid size={4}>
              <FormInputText
                name="firstName"
                label="First Name:"
                control={control}
                helper={rules.firstName}
                InputStyle={DataTextField}
              />
            </Grid>
            <Grid size={9} mt={"2rem"}>
              <FormInputText
                name="email"
                label="Email:"
                control={control}
                helper={rules.emailAddress}
                InputStyle={DataTextField}
              />
            </Grid>
            <Grid size={10} mt={"1rem"} sx={CheckBoxStyle}>
                <Box sx={CheckboxStyle}>
                <Controller
                  control={control}
                  name="isPrivacyCheck"
                  rules={rules.checkBox}
                  render={({
                  field: { onChange },
                  fieldState: { error },
                  }) => (
                  <>
                    <FormControlLabel
                    label={
                      <Typography
                      variant="body1"
                      fontWeight={fontWeight.Weight[4]}
                      color={colors.black[4]}
                      >
                      By clicking I agree, you agree to our Privacy and
                      Website Content statements.
                      </Typography>
                    }
                    control={
                      <Checkbox
                      size="small"
                      checked={isChecked}
                      onChange={(e) => {
                        onChange(e.target.checked);
                        handleChange(e);
                      }}
                      data-testid="security-checkbox"
                      />
                    }
                    />
                    {
                    <FormHelperText>
                      {error ? error.message : null}
                    </FormHelperText>
                    }
                  </>
                  )}
                />
                </Box>
            </Grid>
            <Grid size={3} mt={"1rem"}>
              <Button
                variant="contained"
                disabled={!clickable || isDisagree}
                onClick={handleClick}
              >
                START
              </Button>
            </Grid>
            <Grid size={12} mt={"3rem"} sx={displayFlex}>
              <Typography
                variant="subtitle2"
                fontWeight={fontWeight.Weight[1]}
                color={colors.fonts[11]}
              >
                {`If you are a contracted home health provider in tango's network please use our `}
                <Link
                  variant="subtitle2"
                  fontWeight={fontWeight.Weight[1]}
                  color={colors.fonts[21]}
                  href={providerPortalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ProNet Connect Portal
                </Link>
                {` or to register for our ProNet Connect Portal, email `}
                <Link
                  variant="subtitle2"
                  fontWeight={fontWeight.Weight[1]}
                  color={colors.fonts[21]}
                  href={`mailto:${providerRelationEmail}`}
                >
                  {providerRelationEmail}
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <PrivacySecurityConsentModal
        openModal={openModal}
        closeModal={setOpenModal}
        setIsDisagree={setIsDisagree}
        setIsChecked={setIsChecked}
      />
    </>
  );
};

export default RequestorInformation;
